/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2020, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
*/

import {
  Component, EventEmitter, Inject,
  Input, OnChanges, OnInit,
  Output, SimpleChanges,
} from '@angular/core';
import { generateObjectId } from '../../app/exports';
import { AppConfigService, unsplashConfig } from '../../common/services/app-config/app-config.service';
import { UnitSimpleDTO } from '../../resume/content-selection-modal/unit-simple-dto.model';
import { ButtonSize } from '../aero-button/aero-button.component';

/**
 * Component for our generalized aero block-tile.
 */
@Component({
  selector: 'aero-unit-tile',
  templateUrl: './aero-unit-tile.component.html',
  standalone: false,
})
export class AeroUnitTileComponent implements OnInit, OnChanges {
  /**
   * IMPORTANT NOTE :
   * A LOT of this logic and functions ( methods ) were taken from our image-asset.ts and block-controller.js files.
   * Any change there, should be made here. Would love time to upgrade our AngularJS directives / controllers
   * so we could actually use them, instead of 'hacking' them, in Angular.
   *
   * TODO TAM future : create and inject some sort of Unsplash Service to handle all the logic of setting image values
   * ( id, photographer name, url, etc... ) instead of this component handling it.
   */

  /**
   * The potential image meta info. This is only used for unsplash images.
   */
  @Input() imageMetaInfo ?: any;

  /**
   * Current unit to display.
   */
  @Input() unit : any;

  /**
   * Boolean indicating if aero-unit-tile is being rendered from the content-selection-modal component.
   */
  @Input() renderedInResumeSubSectionView : boolean = false;

  /**
   * Boolean indicating whether or not the tile is being rendered in the edit mode of the resume section.
   */
  @Input() editMode ?: boolean = false;

  /**
   * Set the value as true if the unit is used for selection.
   */
  @Input() selectionMode ?: boolean = false;

  /**
   * Event emitter for when the user selects the whole collection to add.
   */
  @Output() onCollectionSelect : EventEmitter< any > = new EventEmitter< any >();

  /**
   * Event Emitter for when the user removes the selected unit from the resume sub-section.
   */
  @Output() onUnitRemoval : EventEmitter< any > = new EventEmitter< any >();

  /**
   * Unsplash label.
   */
  public unsplashLabel : string = this.AppConfigService.unsplash ? this.AppConfigService.unsplash.label : '';

  private unsplashConfig: unsplashConfig = this.AppConfigService.unsplash;

  /**
   * Photographer URL for the unsplash image.
   *
   * @private
   */
  public photographerProfileUrl = '';

  /**
   * Photographer name for the unsplash image.
   *
   * @private
   */
  public photographerName = '';

  /**
   * Determine what block type the unit is.
   */
  public blockType : Function = this.getBlockType;

  /**
   * Reference Function to Determine if the user can edit the current unit.
   */
  public canEdit : Function = this.canEditUnit;

  /**
   * Reference Function to Determine the unit's cover image aspect ratio.
   */
  public coverImageAspect : Function = this.updateCoverImage;

  /**
   * Reference Function to Determine potential credit type for units' image assets.
   */
  public creditType : Function = this.getCreditType;

  /**
   * Unique id for the unit.
   */
  public uniqueId : string;

  /**
   * Reference Function to Determine if the unit is deleted, sending it to the 'soft' delete state.
   */
  public isDeleted : Function = this.isUnitDeleted;

  /**
   * Image id for unsplash.
   * @private
   */
  private id = '';

  /**
   * Urchin Traffic Monitor URL, used in creating the unsplash image url.
   *
   * @private
   */
  private utmUrl = '';

  /**
   * Unsplash image URL.
   * @private
   */
  private unsplashUrl = '';

  /**
   * Constant for our unsplash name limit.
   *
   * @private
   */
  private static UNSPLASH_NAME_THRESHOLD : number = 10;

  /**
   * Button size of aero button
   */
  public buttonSize : ButtonSize = ButtonSize.SLIM;

  /**
   * Default Constructor.
   *
   * @param BulbAuthService
   * @param BulbAssetLibraryUnsplashService
   * @param BulbModalService
   */
  constructor(
    @Inject('BulbAuthService') private BulbAuthService,
    @Inject('BulbAssetLibraryUnsplashService') private BulbAssetLibraryUnsplashService,
    @Inject('BulbModalService') private BulbModalService,
    private AppConfigService : AppConfigService,
  ) {}

  /**
   * A callback method that is invoked immediately after the default change detector has checked the directive's
   * data-bound properties for the first time, and before any of the view or content children have been checked.
   */
  ngOnInit() {
    if (this.imageMetaInfo && this.imageMetaInfo.source === this.unsplashLabel) {
      this.photographerName = this.trimName(this.imageMetaInfo);
      this.photographerProfileUrl = this.imageMetaInfo.photographerProfileUrl;
      this.BulbAssetLibraryUnsplashService.updateViewCount(this.imageMetaInfo.imageId);
    }
    // if the selectionMode is false then we generate a unique id.
    this.uniqueId = this.selectionMode ? this.unit.id : generateObjectId();
  }

  /**
   * A lifecycle hook that is called when any data-bound property of a component changes.
   *
   * @param changes
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes.imageMetaInfo) {
      if (changes.imageMetaInfo.currentValue) {
        const metaInfo : any = changes.imageMetaInfo.currentValue;
        if (metaInfo.source === this.unsplashLabel) {
          this.photographerName = this.trimName(metaInfo);
          this.utmUrl = `?utm_source=${this.unsplashConfig.client.application.name}&utm_medium=referral`;
          this.photographerProfileUrl = `${metaInfo.photographerProfileUrl}${this.utmUrl}`;
          this.unsplashUrl = `https://unsplash.com${this.utmUrl}`;
          this.id = metaInfo.id;
        }
      }
    }
    if (changes.editMode) {
      if (changes.editMode.currentValue) {
        this.editMode = changes.editMode.currentValue;
      }
    }
  }

  /**
   * Determines a block type from the given unit
   * @param unit current unit
   *
   * @returns String a string constant representing this block's type
   */
  private getBlockType(unit: any): string {
    switch (unit.unitType) {
      case 'CompositeUnit':
        return 'collection';
      case 'ArchiveCompositeUnit':
        return 'archiveCollection';
      case 'DraftPageUnit':
      case 'PublishedPageUnit':
      case 'ArchivePageUnit':
        return 'page';
      case 'GroupUnit':
        return 'group';
      case 'UserUnit':
        return 'user';
      default:
        return unit.unitType;
    }
  }

  /**
   * Get potential credit type for units' cover image.
   *
   * @param unit
   * @private
   */
  private getCreditType(unit : any) : string {
    return (unit.image.source === this.unsplashLabel)
      ? this.unsplashConfig.credittype.icon : this.unsplashConfig.credittype.none;
  }

  /**
   * Determine if the user can edit the current unit.
   *
   * @param unit
   * @private
   */
  private canEditUnit(unit : any) :boolean {
    return ((!this.isUnitDeleted(unit)) && this.BulbAuthService.currentUser.hasPermission(unit, 'UPDATE'));
  }

  /**
   * Determine if the unit is deleted, sending it to the 'soft' delete state.
   *
   * @param unit
   * @private
   */
  private isUnitDeleted(unit : any) : boolean {
    return unit.deleteAfterDays && unit.deleteAfterDays > 0;
  }

  /**
   * Determine if the coverImage is 'wide' or 'tall' based on the aspect ratio of the iamge and the containing
   * figure element, and set the appropriate class so it is correctly centered vertically or horizontally.
   *
   * @param unit
   * @private
   */
  private updateCoverImage(unit : any) : string {
    const coverImage = unit.image;
    if (coverImage) {
      const figure = jQuery('.bulb-block-cover-image-tile');
      if ((coverImage.width / coverImage.height) > (figure.width() / figure.height())) {
        // center coverImage horizontally
        return 'wide';
      }
      // center coverImage vertically
      return 'tall';
    }
    return null;
  }

  /**
   * Generate the correct unit path, based on what render view the content-selection-display component is in.
   */
  public generateUnitPath() : string {
    return `/u/${this.unit.path}`;
  }

  /**
   * Method to trim name.
   *
   * @param imageMetaInfo
   */
  private trimName(imageMetaInfo : any) : string {
    if (imageMetaInfo.assetDefinitionsKey === 'circularImage'
      && imageMetaInfo.photographerName.length >= AeroUnitTileComponent.UNSPLASH_NAME_THRESHOLD) {
      return imageMetaInfo.photographerName.substring(0, AeroUnitTileComponent.UNSPLASH_NAME_THRESHOLD);
    }
    return imageMetaInfo.photographerName;
  }

  /**
   * This method opens the passed url in new window eg: unsplash profile of the photographer.
   * @param url the url to be opened in new tab.
   */
  public openUrlInNewWindow(url : string) : void {
    window.open(url, '_blank');
    event.stopPropagation();
    event.preventDefault();
  }

  /**
   * Method that selects the whole collection to add to the user's portfolio content.
   *
   * @param event
   * @param entireCollection
   */
  public selectCollection(event : any, entireCollection : boolean) : void {
    this.onCollectionSelect.emit({ entireCollection });
    event.stopPropagation();
    event.preventDefault();
  }

  /**
   * Method that deletes the selected portfolio content from the resume sub-section.
   *
   * @param event
   * @param unit unit to remove from the portfolio content.
   */
  public deleteSelectedPortfolioContent(event : any, unit : UnitSimpleDTO) : void {
    event.stopPropagation();
    event.preventDefault();
    const removePortfolioContentModalOptions: any = {
      fontAwesomeCode: 'fas fa-times-circle',
      titleCode: 'resume_remove_portfolio_content_title',
      bodyCode: 'resume_remove_portfolio_content_body',
      okButtonCode: 'button_remove',
      cancelButtonCode: 'button_cancel',
      showCancelButton: true,
      windowClass: 'updated-modal-design bulb-aero-modal remove-resume-portfolio-content-modal',
    };
    this.BulbModalService.show(removePortfolioContentModalOptions).then(() => {
      this.onUnitRemoval.emit({ removePortfolioContent: true, unit });
    });
  }
}
